import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import GroupedSidebarNav from "components/GroupedSidebarNav";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import linguistApi, { waitForLinguistAuthUser } from "services/linguistApi";
import { auth } from "../../firebase/firebase";
import { NAVBAR_HEIGHT } from "constants/layout";
import { parsePhoneNumber } from "libphonenumber-js";
import { getAvatarColor, getAvatarInitials } from "helpers/avatarHelpers";
import { LINGUIST_ONBOARDING_RESTART_EVENT } from "helpers/linguistOnboarding";
import { usePageTitle } from "hooks/usePageTitle";

// Helper: format a phone number (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    const normalizedRaw = String(raw).trim();
    const withPlus = normalizedRaw.startsWith("+") ? normalizedRaw : `+${countryCode}${normalizedRaw}`;
    const phoneNumber = parsePhoneNumber(withPlus);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY = "ce_portal.browserNotificationsEnabled";
const SMS_NOTIFICATION_ENABLED_STORAGE_KEY = "ce_portal.smsNotificationsEnabled";
const QUIET_HOURS_START_STORAGE_KEY = "ce_portal.quietHoursStart";
const QUIET_HOURS_END_STORAGE_KEY = "ce_portal.quietHoursEnd";
const TIMEZONE_STORAGE_KEY = "ce_portal.notificationTimezone";

const DEFAULT_QUIET_START = "22:00";
const DEFAULT_QUIET_END = "07:00";

const LINGUIST_SETTINGS_NAV_GROUPS = [
  {
    title: "Account",
    items: [
      { label: "Profile", value: 0 },
      { label: "Notifications", value: 1 },
    ],
  },
];

const readBooleanSetting = (key, fallback = true) => {
  try {
    const value = window.localStorage.getItem(key);
    if (value === null) return fallback;
    return value !== "false";
  } catch (error) {
    return fallback;
  }
};

const readStringSetting = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(key);
    return value || fallback;
  } catch (error) {
    return fallback;
  }
};

const getDefaultTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch (error) {
    return "America/New_York";
  }
};

const formatTime12Hour = (timeValue) => {
  const normalized = String(timeValue || "").trim();
  const [rawHour, rawMinute] = normalized.split(":");
  const hour = Number(rawHour);
  const minute = Number(rawMinute);

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return normalized || "N/A";
  }

  const safeHour = ((hour % 24) + 24) % 24;
  const period = safeHour >= 12 ? "PM" : "AM";
  const hour12 = safeHour % 12 === 0 ? 12 : safeHour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
};

const SettingsCard = ({ children, theme, sx }) => (
  <Box
    sx={{
      bgcolor: theme.palette.background.default,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.primary.border2}`,
      p: { xs: "0.75rem", sm: "1.25rem" },
      mx: "0.5rem",
      mt: "0.25rem",
      boxShadow: theme.shadows[3],
      maxWidth: "100%",
      boxSizing: "border-box",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const LinguistProfile = () => {
  usePageTitle("Profile");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { refreshPage, setOverlayName, setOverlayData } = useStateContext();

  const [tabValue, setTabValue] = useState(0);
  const [profile, setProfile] = useState({
    id: null,
    Name: "",
    Email: "",
    PhoneNumber: "",
    PhoneNumbers: [],
    Status: "",
    Active: true,
    AddressLine1: "",
    AddressLine2: "",
    AddressLine3: "",
    Certifications: "",
  });
  const [settings, setSettings] = useState({
    BrowserNotificationsEnabled: true,
    SmsNotificationsEnabled: false,
    QuietHoursStart: DEFAULT_QUIET_START,
    QuietHoursEnd: DEFAULT_QUIET_END,
    Timezone: getDefaultTimezone(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const authRetryCountRef = useRef(0);

  const visibleTabValues = useMemo(() => {
    return LINGUIST_SETTINGS_NAV_GROUPS.flatMap((group) =>
      group.items.map((item) => item.value),
    );
  }, []);

  const hydrateFromServer = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await linguistApi.get("/me");
      setProfile({
        id: response.data?.id || null,
        Name: response.data?.Name || "",
        Email: response.data?.Email || "",
        PhoneNumber: response.data?.PhoneNumber || "",
        PhoneNumbers: response.data?.PhoneNumbers || [],
        Status: response.data?.Status || "",
        Active: response.data?.Active !== false,
        AddressLine1: response.data?.AddressRecord?.AddressLine1 || "",
        AddressLine2: response.data?.AddressRecord?.AddressLine2 || "",
        AddressLine3: response.data?.AddressRecord?.AddressLine3 || "",
        Certifications: response.data?.Certifications || "",
        Image: response.data?.Image || "",
        ImageThumbnail: response.data?.ImageThumbnail || "",
      });

      setSettings({
        BrowserNotificationsEnabled:
          response.data?.NotificationSettings?.BrowserNotificationsEnabled ??
          readBooleanSetting(BROWSER_NOTIFICATION_ENABLED_STORAGE_KEY, true),
        SmsNotificationsEnabled:
          response.data?.NotificationSettings?.SmsNotificationsEnabled ??
          readBooleanSetting(SMS_NOTIFICATION_ENABLED_STORAGE_KEY, false),
        QuietHoursStart:
          response.data?.NotificationSettings?.QuietHoursStart ||
          readStringSetting(QUIET_HOURS_START_STORAGE_KEY, DEFAULT_QUIET_START),
        QuietHoursEnd:
          response.data?.NotificationSettings?.QuietHoursEnd ||
          readStringSetting(QUIET_HOURS_END_STORAGE_KEY, DEFAULT_QUIET_END),
        Timezone:
          response.data?.NotificationSettings?.Timezone ||
          readStringSetting(TIMEZONE_STORAGE_KEY, getDefaultTimezone()),
      });

      authRetryCountRef.current = 0;
    } catch (requestError) {
      const code = String(requestError?.code || "");
      if (
        code === "LINGUIST_AUTH_USER_UNAVAILABLE" ||
        code === "LINGUIST_AUTH_TOKEN_UNAVAILABLE"
      ) {
        const currentUser = auth.currentUser || (await waitForLinguistAuthUser(2500));
        if (!currentUser) {
          setError(
            "Your session could not be verified right now. Please sign in again if this continues.",
          );
          setLoading(false);
          return;
        }

        if (authRetryCountRef.current < 2) {
          authRetryCountRef.current += 1;
          setTimeout(() => {
            hydrateFromServer();
          }, 700);
          return;
        }

        setError("We are still finalizing your secure session. Please refresh this page once.");
        setLoading(false);
        return;
      }

      setError(
        requestError?.response?.data?.error ||
          "Unable to load your profile right now. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrateFromServer();
  }, [hydrateFromServer, refreshPage]);

  useEffect(() => {
    if (visibleTabValues.length === 0) return;
    if (visibleTabValues.includes(tabValue)) return;
    setTabValue(visibleTabValues[0]);
  }, [tabValue, visibleTabValues]);

  const openProfileEditor = () => {
    setOverlayData({ profile });
    setOverlayName(OVERLAY_NAMES.LINGUIST_PORTAL_PROFILE_EDIT);
  };

  const openNotificationEditor = () => {
    setOverlayData({ settings });
    setOverlayName(OVERLAY_NAMES.LINGUIST_PORTAL_NOTIFICATION_SETTINGS_EDIT);
  };

  if (loading) {
    return (
      <Box
        p="1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4">Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Box
      p={isMobile ? "1rem 1rem 0 1rem" : "1rem 1rem 0 0"}
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          mt: "1rem",
          mx: isMobile ? "0.5rem" : "0rem",
          pb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <GroupedSidebarNav
          groups={LINGUIST_SETTINGS_NAV_GROUPS}
          value={tabValue}
          onChange={setTabValue}
          sx={{
            minWidth: { xs: "100%", sm: 240 },
            maxHeight: { xs: "none", sm: "100%" },
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {error ? <Alert severity="error" sx={{ mx: "0.5rem", mb: 1 }}>{error}</Alert> : null}

          {tabValue === 0 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
                      Profile
                    </Typography>
                    <IconButton
                      aria-label="Edit profile"
                      onClick={openProfileEditor}
                      sx={(currentTheme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: currentTheme.palette.background.default,
                        border: "1px solid",
                        borderColor: currentTheme.palette.divider,
                        color: currentTheme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: currentTheme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: currentTheme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <EditRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Photo
                      </Typography>
                    </Box>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: getAvatarColor(profile.Name),
                        borderRadius: "12px",
                        boxShadow: theme.shadows[3],
                      }}
                      src={profile.ImageThumbnail || profile.Image || undefined}
                    >
                      {getAvatarInitials(profile.Name)}
                    </Avatar>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                    </Box>
                    <Typography variant="body1">{profile.Name || "-"}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Email
                      </Typography>
                    </Box>
                    <Typography variant="body1">{profile.Email || "-"}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                    </Box>
                    <Typography variant="body1">{
                    profile.PhoneNumbers && profile.PhoneNumbers.length > 0
                      ? profile.PhoneNumbers
                          .sort((a, b) => (b.IsPrimary ? 1 : 0) - (a.IsPrimary ? 1 : 0) || a.SortOrder - b.SortOrder)
                          .map((p) => {
                            const cc = p.CountryCode || "1";
                            const formatted = p.Number ? formatPhoneNumber(p.Number, cc) : "";
                            const ext = p.Ext ? ` x${p.Ext}` : "";
                            const label = p.Label ? ` (${p.Label})` : "";
                            return `${formatted}${ext}${label}`;
                          })
                          .join(" | ")
                      : "-"
                  }</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                    </Box>
                    <Typography variant="body1">{profile.Status || "-"}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Account
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      color={profile.Active ? "success.main" : "error.main"}
                    >
                      {profile.Active ? "Active" : "Disabled"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1">{profile.AddressLine1 || "-"}</Typography>
                      {profile.AddressLine2 ? <Typography variant="body1">{profile.AddressLine2}</Typography> : null}
                      {profile.AddressLine3 ? <Typography variant="body1">{profile.AddressLine3}</Typography> : null}
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" color="text.secondary">
                        Certifications
                      </Typography>
                    </Box>
                    <Typography variant="body1">{profile.Certifications || "-"}</Typography>
                  </Box>
                </Box>
              </Box>
            </SettingsCard>
          )}

          {tabValue === 1 && (
            <SettingsCard
              theme={theme}
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: 0,
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
                      Notifications
                    </Typography>
                    <IconButton
                      aria-label="Edit notifications"
                      onClick={openNotificationEditor}
                      sx={(currentTheme) => ({
                        height: 36,
                        width: 36,
                        p: 0,
                        borderRadius: "12px",
                        backgroundColor: currentTheme.palette.background.default,
                        border: "1px solid",
                        borderColor: currentTheme.palette.divider,
                        color: currentTheme.palette.primary.main,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: currentTheme.palette.primary.main,
                        },
                        "&:focus-visible": {
                          borderColor: currentTheme.palette.primary.main,
                          outline: "none",
                        },
                      })}
                    >
                      <EditRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        Browser Notifications
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {settings.BrowserNotificationsEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        SMS Notifications
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {settings.SmsNotificationsEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        Quiet Hours
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {formatTime12Hour(settings.QuietHoursStart || DEFAULT_QUIET_START)} - {" "}
                      {formatTime12Hour(settings.QuietHoursEnd || DEFAULT_QUIET_END)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        Timezone
                      </Typography>
                    </Box>
                    <Typography variant="body1">{settings.Timezone || getDefaultTimezone()}</Typography>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        Portal Tour
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent(LINGUIST_ONBOARDING_RESTART_EVENT),
                        )
                      }
                    >
                      Take the tour again
                    </Button>
                  </Box>
                </Box>
              </Box>
            </SettingsCard>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistProfile;
